import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Download, X } from "lucide-react";

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // Show iOS prompt after a delay if on iOS and not installed
    if (isIOSDevice && !navigator.standalone) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    // Don't show again for this session
    sessionStorage.setItem("installPromptDismissed", "true");
  };

  // Check if dismissed this session
  useEffect(() => {
    if (sessionStorage.getItem("installPromptDismissed")) {
      setShowPrompt(false);
    }
  }, []);

  if (!showPrompt) return null;

  return (
    <>
      {/* Install Banner */}
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-card border border-border rounded-xl shadow-xl p-4 z-50 slide-up">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          aria-label="Stäng"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
            <Download className="w-6 h-6 text-primary-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-sm" style={{ fontFamily: "Manrope, sans-serif" }}>
              Installera Tidkraft
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Lägg till appen på din hemskärm för snabb åtkomst
            </p>
            <Button
              onClick={handleInstall}
              size="sm"
              className="mt-3 w-full"
              data-testid="install-app-btn"
            >
              {isIOS ? "Visa instruktioner" : "Installera"}
            </Button>
          </div>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl max-w-sm w-full p-6 slide-up">
            <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
              Installera på iPhone/iPad
            </h3>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                  1
                </span>
                <span>
                  Tryck på <strong>Dela</strong>-knappen{" "}
                  <span className="inline-block w-5 h-5 bg-muted rounded text-center leading-5">↑</span>{" "}
                  i Safari
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                  2
                </span>
                <span>Scrolla ner och tryck på <strong>"Lägg till på hemskärmen"</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                  3
                </span>
                <span>Tryck på <strong>"Lägg till"</strong> uppe till höger</span>
              </li>
            </ol>
            <Button onClick={handleDismiss} className="w-full mt-6">
              Jag förstår
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
